import { useCallback, useSyncExternalStore } from "react";

/**
 * Session-scoped expand/collapse memory for the consolidated rail, so the tree
 * you drilled into is still open when you navigate away and back or switch
 * rails (which unmounts the whole rail content). Deliberately held in module
 * memory, NOT persisted: a full page load starts the rail from its defaults
 * (Favorites + Workspaces open, everything else collapsed) — the memory exists
 * to survive in-app navigation, not browser restarts. `useSyncExternalStore`
 * keeps duplicate rows of the same resource (e.g. a canvas in Favorites and in
 * its workspace) in sync.
 */

type ExpansionMap = Record<string, boolean>;

const cache: ExpansionMap = {};
const listeners = new Set<() => void>();

function write(key: string, open: boolean) {
  if (cache[key] === open) return;
  cache[key] = open;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Expansion state for one rail node. `key` must be stable and unique per
 * visual location (include the surface when the same resource can appear in
 * several places and should remember each independently).
 */
export function useRailExpansion(
  key: string,
  defaultOpen = false,
): readonly [boolean, (open: boolean) => void] {
  const open = useSyncExternalStore(subscribe, () => cache[key] ?? defaultOpen);
  const setOpen = useCallback((next: boolean) => write(key, next), [key]);
  return [open, setOpen] as const;
}

/**
 * Variant for accordion groups (an array of open group ids), stored in the
 * same session memory as per-group booleans.
 */
export function useRailExpansionGroups(
  groupIds: readonly string[],
  defaultOpenIds: readonly string[],
): readonly [string[], (open: string[]) => void] {
  const groupsKey = groupIds.join(" ");
  const defaultsKey = defaultOpenIds.join(" ");

  const snapshot = useSyncExternalStore(subscribe, () => {
    const defaults = defaultsKey === "" ? [] : defaultsKey.split(" ");
    return (groupsKey === "" ? [] : groupsKey.split(" "))
      .filter((id) => cache[`group:${id}`] ?? defaults.includes(id))
      .join(" ");
  });
  const value = snapshot === "" ? [] : snapshot.split(" ");

  const setValue = useCallback(
    (open: string[]) => {
      for (const id of groupsKey === "" ? [] : groupsKey.split(" ")) {
        write(`group:${id}`, open.includes(id));
      }
    },
    [groupsKey],
  );

  return [value, setValue] as const;
}
